import { useState, useEffect } from 'react';

interface NoteEntry {
  source: string;
  timestamp: string;
  text: string;
  vendor?: string;
  user_email?: string;
}

interface NotesEditorProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}

/**
 * A user-friendly editor for outcome_notes that displays individual note entries
 * with add/delete functionality instead of raw JSON editing.
 */
export function NotesEditor({ value, onChange, error }: NotesEditorProps) {
  const [notes, setNotes] = useState<NoteEntry[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  // Parse JSON value into notes array
  useEffect(() => {
    if (!value || value.trim() === '') {
      setNotes([]);
      setParseError(null);
      return;
    }

    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        setNotes(parsed);
        setParseError(null);
      } else {
        setParseError('Notes data is not an array');
      }
    } catch {
      setParseError('Invalid JSON format');
    }
  }, [value]);

  // Serialize notes back to JSON and notify parent
  const updateNotes = (newNotes: NoteEntry[]) => {
    setNotes(newNotes);
    onChange(JSON.stringify(newNotes, null, 2));
  };

  const handleTextChange = (index: number, newText: string) => {
    const updated = [...notes];
    updated[index] = { ...updated[index], text: newText };
    updateNotes(updated);
  };

  const handleDelete = (index: number) => {
    const updated = notes.filter((_, i) => i !== index);
    updateNotes(updated);
  };

  const handleAddNote = () => {
    const newNote: NoteEntry = {
      source: 'app_user',
      timestamp: new Date().toISOString(),
      text: '',
    };
    updateNotes([...notes, newNote]);
  };

  const formatSourceLabel = (note: NoteEntry): string => {
    if (note.source === 'vendor') {
      return `Vendor: ${note.vendor || 'Unknown'}`;
    }
    if (note.source === 'app_user') {
      return `User: ${note.user_email || 'You'}`;
    }
    return 'Migration';
  };

  const formatTimestamp = (timestamp: string): string => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  if (parseError) {
    return (
      <div>
        <div style={{
          padding: 'var(--spacing-sm)',
          backgroundColor: 'var(--color-bg-danger)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 'var(--spacing-sm)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-danger)',
        }}>
          {parseError}. Showing raw JSON editor:
        </div>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={8}
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            borderColor: error ? 'var(--color-danger)' : undefined,
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
      {notes.length === 0 ? (
        <div style={{
          padding: 'var(--spacing-md)',
          backgroundColor: 'var(--color-bg-tertiary)',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
        }}>
          No notes yet
        </div>
      ) : (
        notes.map((note, index) => (
          <div
            key={index}
            style={{
              padding: 'var(--spacing-sm)',
              backgroundColor: note.source === 'vendor'
                ? 'var(--color-bg-info)'
                : 'var(--color-bg-tertiary)',
              borderRadius: 'var(--radius-sm)',
              borderLeft: `3px solid ${note.source === 'vendor' ? 'var(--color-primary)' : 'var(--color-border)'}`,
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--spacing-xs)',
            }}>
              <div style={{
                display: 'flex',
                gap: 'var(--spacing-md)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
              }}>
                <span style={{ fontWeight: 500 }}>{formatSourceLabel(note)}</span>
                <span>{formatTimestamp(note.timestamp)}</span>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(index)}
                style={{
                  padding: '2px 8px',
                  fontSize: 'var(--font-size-xs)',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                }}
                title="Delete this note"
              >
                Delete
              </button>
            </div>
            <textarea
              value={note.text}
              onChange={(e) => handleTextChange(index, e.target.value)}
              rows={3}
              placeholder="Enter note text..."
              style={{
                width: '100%',
                resize: 'vertical',
                borderColor: error ? 'var(--color-danger)' : undefined,
              }}
            />
          </div>
        ))
      )}

      <button
        type="button"
        onClick={handleAddNote}
        style={{
          padding: 'var(--spacing-sm) var(--spacing-md)',
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          color: 'var(--color-text-secondary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--spacing-xs)',
        }}
      >
        + Add Note
      </button>
    </div>
  );
}
